import {HttpClient} from '@angular/common/http';
import {Injectable, NgZone} from '@angular/core';
import {Router} from '@angular/router';
import {UntilDestroy} from '@jumio/portals.core';
import {BaseErrorCodes} from 'public-shared/models/error/base-error-codes';
import {ErrorDescriptor} from 'public-shared/models/error/error-descriptor';
import {LoginUrls} from 'public-shared/interfaces/route-url-constants';
import {LoginResult, UserContext} from 'public-shared/services/auth/login-result.dto';
import {PublicAuthService} from 'public-shared/services/auth/public-auth.service';
import {firstValueFrom, Observable, Subscription, throwError} from 'rxjs';
import {catchError, tap} from 'rxjs/operators';
import {AdminSSOMappingRequest, AdminSSOMappingResponse, OAuth2Code} from 'shared/services/admin-sso-auth/admin-sso-auth.dto';
import {TfaRequest} from 'shared/services/auth/tfa-request.dto';
import {CacheService} from 'shared/services/common/cache.service';
import {AuthEndpoints, OAuth2EndpointBASE, OAuth2UserMapping} from 'shared/services/endpoint-constants';
import {SecurityContextHolder} from 'shared/services/security/security-context-holder';
import {RedirectService} from '../redirect/redirect-service.service';

@UntilDestroy()
@Injectable()
export class AuthService extends PublicAuthService {
  public readonly subscription = new Subscription();

  constructor(
    protected override securityContextHolder: SecurityContextHolder,
    protected override http: HttpClient,
    private router: Router,
    private cache: CacheService,
    private zone: NgZone,
    private redirectService: RedirectService
  ) {
    super(securityContextHolder, http);
  }

  public logout(): void {
    this.logoutCalled = true;
    if (this.securityContextHolder.isLoggedIn) {
      this.subscription.add(
        this.delete$<void>(AuthEndpoints.ACCESS_TOKEN).subscribe(
          () => this.doLogout(),
          () => this.doLogout()
        )
      );
    } else {
      this.doLogout();
    }
  }

  async logoutAuth0(accessToken: string): Promise<void> {
    if (this.securityContextHolder.accessToken) {
      await firstValueFrom(this.logoutAuth0$(accessToken));
    }
  }

  public get isLogoutCalled(): boolean {
    return this.logoutCalled;
  }

  /**
   * Sends the security code confirmation to the backend.
   * @param {string} passCode The security code as a string.
   * @returns {Observable<void>} The result as an Observable.
   */
  public setTfapassCode$(passCode: string): Observable<void> {
    return this.post$<void>(AuthEndpoints.TFA, new TfaRequest(this.securityContextHolder.accessToken, passCode)).pipe(
      tap(() => {
        // Set 'IsFirstSetupTfaQrCode' to null
        this.securityContextHolder.setIsFirstSetupTfaQrCode(null);
      })
    );
  }

  private doLogout(): void {
    this.zone.run(() => {
      this.cache.purge();
      this.securityContextHolder.invalidate();
      this.redirectService.redirectToLogin();
    });
  }

  public oauth$(payload: AdminSSOMappingRequest): Observable<AdminSSOMappingResponse> {
    return this.post$<AdminSSOMappingResponse>(OAuth2EndpointBASE, payload);
  }

  public verifyCode$(payload: OAuth2Code): Observable<LoginResult> {
    return this.post$<any>(OAuth2UserMapping.CODE, payload).pipe(
      tap((loginResult: LoginResult) => {
        // SSO don't return name and email, so we need to add it manually
        // const userContext = Object.assign(loginResult, {name: loginResult.username, email: loginResult.username});
        this.validateContext(loginResult);
      }),
      catchError((error: ErrorDescriptor) => {
        if (error.errorCode === BaseErrorCodes.ACCOUNT_MIGRATED) {
          this.router.navigateByUrl(`/${LoginUrls.ACCESS_RESTRICTED}`);
        }
        return throwError(() => error);
      })
    );
  }

  public verifyToken$(payload: any): Observable<LoginResult> {
    return this.post$<any>(OAuth2UserMapping.TOKEN, payload);
  }

  public refreshAccessToken$(accessToken: string, refreshToken: string): Observable<UserContext> {
    return this.post$<UserContext>(OAuth2UserMapping.REFRESH_TOKEN, {accessToken, refreshToken});
  }

  public logoutAuth0$(accessToken: string): Observable<{redirectUrl: string}> {
    return this.post$<{redirectUrl: string}>(OAuth2UserMapping.LOGOUT, {accessToken});
  }
}
